import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles, Check, ArrowRight, Crown } from 'lucide-react';
import { Button } from './ui/button';

/**
 * SubscriptionRequired - Shows upgrade prompt when user needs subscription
 * 
 * Can be used in two ways:
 * 1. As a wrapper that blocks content: <SubscriptionRequired><ProtectedContent /></SubscriptionRequired>
 * 2. As a standalone component: <SubscriptionRequired featureName="AI Generation" />
 * 
 * Props:
 * - hasAccess: boolean - if true, renders children
 * - featureName: string - name of feature being blocked
 * - showCompact: boolean - show inline compact version
 * - requiresPro: boolean - show Pro upgrade message instead of general subscription
 * - currentTier: string - user's current tier for context
 */
const SubscriptionRequired = ({ 
  children, 
  hasAccess = false, 
  featureName,
  showCompact = false,
  requiresPro = false,
  currentTier = null
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';

  // If user has access, render children
  if (hasAccess) {
    return children || null;
  }

  // Pro upgrade message (for Start users trying to access Pro features)
  if (requiresPro && currentTier === 'start') {
    if (showCompact) {
      return (
        <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-purple-400" />
              <p className="text-sm text-purple-200">
                {isNorwegian 
                  ? `${featureName || 'Denne funksjonen'} krever Pro-abonnement` 
                  : `${featureName || 'This feature'} requires Pro subscription`}
              </p>
            </div>
            <Button 
              size="sm"
              onClick={() => navigate('/subscription')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
            >
              {isNorwegian ? 'Oppgrader til Pro' : 'Upgrade to Pro'}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-[50vh] flex items-center justify-center p-8">
        <div className="max-w-lg w-full">
          <div className="bg-slate-900 border border-purple-500/30 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center">
              <Crown className="w-8 h-8 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">
              {isNorwegian ? 'Pro-abonnement kreves' : 'Pro Subscription Required'}
            </h2>
            <p className="text-slate-400 mb-6">
              {isNorwegian 
                ? `${featureName || 'Denne funksjonen'} er kun tilgjengelig med Pro-abonnement. Oppgrader for å få tilgang til ISO, Miljøfyrtårn og mer.`
                : `${featureName || 'This feature'} is only available with Pro subscription. Upgrade to access ISO, Miljøfyrtårn and more.`}
            </p>
            <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-white">
                  {isNorwegian ? 'Pro inkluderer:' : 'Pro includes:'}
                </span>
              </div>
              <ul className="space-y-2">
                {[
                  isNorwegian ? 'ISO 9001, 14001 og 45001 moduler' : 'ISO 9001, 14001 and 45001 modules',
                  isNorwegian ? 'Miljøfyrtårn-sertifisering' : 'Miljøfyrtårn certification',
                  isNorwegian ? 'Ubegrenset AI-generering' : 'Unlimited AI generation',
                  isNorwegian ? 'Opptil 15 brukere' : 'Up to 15 users',
                  isNorwegian ? 'Avanserte rapporter' : 'Advanced reports',
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <Button 
              onClick={() => navigate('/subscription')}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white h-12"
            >
              {isNorwegian ? 'Oppgrader til Pro' : 'Upgrade to Pro'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="mt-4 text-sm text-slate-500">
              {isNorwegian ? '549 kr/mnd' : '549 NOK/month'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Compact version for inline display (no subscription at all)
  if (showCompact) {
    return (
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-amber-400" />
            <p className="text-sm text-amber-200">
              {isNorwegian 
                ? `Oppgrader for å bruke ${featureName || 'denne funksjonen'}` 
                : `Upgrade to use ${featureName || 'this feature'}`}
            </p>
          </div>
          <Button 
            size="sm"
            onClick={() => navigate('/subscription')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            {isNorwegian ? 'Se priser' : 'See prices'}
          </Button>
        </div>
      </div>
    );
  }

  // Full page/section version (no subscription)
  return (
    <div className="min-h-[50vh] flex items-center justify-center p-8">
      <div className="max-w-lg w-full">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center">
            <Lock className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            {isNorwegian ? 'Abonnement kreves' : 'Subscription Required'}
          </h2>
          <p className="text-slate-400 mb-6">
            {isNorwegian 
              ? `Du trenger et aktivt abonnement for å bruke ${featureName || 'denne funksjonen'}. Velg et abonnement for å komme i gang.`
              : `You need an active subscription to use ${featureName || 'this feature'}. Choose a subscription to get started.`}
          </p>
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-white">
                {isNorwegian ? 'Med abonnement får du:' : 'With a subscription you get:'}
              </span>
            </div>
            <ul className="space-y-2">
              {[
                isNorwegian ? 'Komplett IK-system med AI-generering' : 'Complete IC system with AI generation',
                isNorwegian ? 'Avvikshåndtering og oppgavestyring' : 'Deviation handling and task management',
                isNorwegian ? 'Oppgaver knyttet til IK § 5' : 'Tasks related to IC § 5',
                isNorwegian ? 'Eksport til Word/PDF' : 'Export to Word/PDF',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-slate-300">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <Button 
            onClick={() => navigate('/subscription')}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white h-12"
          >
            {isNorwegian ? 'Se abonnementer' : 'View subscriptions'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="mt-4 text-sm text-slate-500">
            {isNorwegian ? 'Fra kun 299 kr/mnd' : 'Starting at just 299 NOK/month'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequired;
